import * as PIXI from 'pixi.js';
import GameConfig from '../../game/GameConfig';
import GameModel from '../../game/GameModel';
import {MapState} from '../../common/map/MapState';
import GameUtils from 'src/game/GameUtils';
import {buildingTintForPlayerIndex} from '../player-colors';
import {Location, Loc} from '../../common/map/Location';

import Sprite = PIXI.Sprite;

type WallState = 'damaged' | string;

interface WallTile {
    state: WallState;
    sprite: Sprite;
}

export class WallRenderer {
    private tiles: Map<string, WallTile> = new Map<string, WallTile>();
    private map: MapState;

    public buildWallPiece(tiles: Location[]): void {
        this.updateTiles(tiles);
    }

    public damageWall(x: number, y: number): void {
        this.updateTiles([new Location(x, y)]);
    }

    public redraw(): void {
        this.map.forEachTile(loc => this.updateTile(loc));
    }

    public setMap(map: MapState): void {
        if (this.map === map) return;
        this.map = map;
        this.redraw();
    }

    public clear(): void {
        this.tiles.forEach(tile => GameModel.wallsContainer.removeChild(tile.sprite));
        this.tiles.clear();
    }

    public updateTiles(tiles: Location[]): void {
        const toUpdate = new Map<string, Location>();
        tiles.forEach(tile => {
            const locs = [tile, ...this.map.valid4Neighbors(tile)];
            locs.forEach(loc => toUpdate.set(loc.toString(), loc));
        });
        toUpdate.forEach(loc => this.updateTile(loc));
    }

    private updateTile(loc: Location): void {
        const key = loc.toString();
        const tile = this.tiles.get(key);
        const wall = this.map.walls[loc.y][loc.x];
        if (wall) {
            const state = this.wallState(loc);
            if (tile) {
                if (tile.state === state) return;
                tile.state = state;
                tile.sprite.texture = GameUtils.spriteTexture(`walls/${state}`);
            } else {
                const texture = GameUtils.spriteTexture(`walls/${state}`);
                const sprite = new Sprite(texture);
                const size = GameConfig.tileSize;
                sprite.position.set(loc.x * size, loc.y * size);
                sprite.tint = buildingTintForPlayerIndex(wall.owner);
                GameModel.wallsContainer.addChild(sprite);
                this.tiles.set(key, {state, sprite});
            }
        } else if (tile) {
            GameModel.wallsContainer.removeChild(tile.sprite);
            this.tiles.delete(key);
        }
    }

    private wallState({x, y}: Loc): WallState {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const {damaged, owner} = this.map.walls[y][x]!;
        if (damaged) {
            return 'damaged';
        }

        const left = this.map.isPlayerWall(new Location(x - 1, y), owner) ? '1' : '0';
        const up = this.map.isPlayerWall(new Location(x, y - 1), owner) ? '1' : '0';
        const right = this.map.isPlayerWall(new Location(x + 1, y), owner) ? '1' : '0';
        const down = this.map.isPlayerWall(new Location(x, y + 1), owner) ? '1' : '0';
        const base = `${left}${up}${right}${down}`;

        if (base === '0101' && y % 2 === 0) return '0101-alt';
        if (base === '1010' && x % 2 === 0) return '1010-alt';

        if (base === '0000' || base === '0001') {
            return '1001';
        } else if (base === '0010' || base === '0100' || base === '1000') {
            return base.substr(0, 3) + '1';
        }

        return base;
    }
}

export const wallRenderer = new WallRenderer();



// WEBPACK FOOTER //
// ./src/Pixi/map/WallRenderer.ts